/**
 * iter-72 — Channel domain module extracted from /lib/audioEngine.js
 *
 * A single mixer channel owns:
 *   • AudioBuffer (or null) for the primary clip
 *   • Per-channel FX chain (autotune / EQ / comp / reverb / delay / limiter)
 *   • Sidechain ducker (audio mode or LFO mode)
 *   • Volume / pan / mute / solo / pitch-bend state
 *   • Phase-(c) "clips" — additional positioned audio segments
 *   • Plugin chain (AudioWorklet-based VSTs)
 *   • Peak meter (via AnalyserNode)
 *   • Automation history + replay cursor
 *
 * Public surface is EXACTLY the same as the pre-iter-72 inline Channel class —
 * audioEngine.js re-exports this module's `Channel` and `downsample` so any
 * external code that does `import { Channel } from "@/lib/audioEngine"` still
 * resolves correctly. See snapshotHarness.js for the audible-behaviour
 * guarantees this refactor preserves.
 */
import { FX_PARAM_DEFAULTS, buildFxChain } from "@/lib/audioEngine/fxRecipes";

export class Channel {
  // Module-level counter for unique clip ids — needs to outlive every channel
  // instance so ids never collide between channels.
  static _clipSeq = 0;

  constructor(ctx, masterIn, sharedReverbIR) {
    this.ctx = ctx;
    this.buffer = null;
    this.name = null;
    // iter-77 — channel group. When non-null, this channel is part of a
    // mute/solo-linked group (set by SongLibrary.handleBounceLineage when
    // bouncing a derivation lineage to the mixer). Soloing or muting any
    // channel in a group propagates to every other channel in the same
    // group so the user can A/B the whole chain with one click.
    // `groupColor` is a CSS color string used by the mixer UI to render a
    // vertical stripe identifying the group.
    this.groupId = null;
    this.groupColor = null;
    this.duration = 0;
    this.source = null;
    // Phase-(c) — clip-based arrangement support. `clips[]` holds positioned
    // audio segments rendered alongside the primary buffer. `clipSources[]`
    // is the per-play list of active BufferSourceNodes (cleared on stop()).
    this.clips = [];
    this.clipSources = [];
    // `engineSecPerBeat` is set by the parent engine whenever tempo changes
    // so the channel's _scheduleClips can convert startBeat → seconds.
    this.engineSecPerBeat = 60 / 120;  // 120 BPM default until engine sets

    this.gainNode = ctx.createGain();
    this.gainNode.gain.value = 0.72;

    // Sidechain ducker — sits between FX chain and volume so it modulates
    // the wet signal but is INDEPENDENT of the user's volume fader. Default
    // gain is 1.0 (no ducking) until another channel is set as the trigger.
    // Driven by tickSidechain() in the engine's animation loop: reads the
    // trigger channel's peak via getPeak() → drives this gain to (1 - depth)
    // when peak exceeds threshold, recovers to 1.0 in release time.
    this.sidechainGain = ctx.createGain();
    this.sidechainGain.gain.value = 1.0;
    this.sidechain = {
      // mode: "audio" = read trigger.getPeak() each frame.
      //       "lfo"   = generate a tempo-synced rhythmic duck (kick replacement);
      //                lfoSubdivision is in beats (1 = 1/4 note, 0.5 = 1/8, etc.)
      mode: "audio",
      keyChannel: null,   // index of trigger channel (audio mode only), or null = bypassed
      threshold: 0.30,    // 0..1 — peak level above which ducking engages
      depth: 0.65,        // 0..1 — how much to duck (0 = no duck, 1 = -inf)
      attack: 0.005,      // seconds — time constant for engaging the duck
      release: 0.12,      // seconds — time constant for recovery
      lfoSubdivision: 1,  // 1 = quarter, 0.5 = eighth, 0.25 = sixteenth, 2 = half
    };

    this.panNode = ctx.createStereoPanner();
    this.panNode.pan.value = 0;

    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 256;
    this.peakBuffer = new Uint8Array(this.analyser.frequencyBinCount);

    // FX chain feeds INTO sidechainGain → gainNode (channel volume) → pan → analyser → master
    this.fxChain = buildFxChain(ctx, this.sidechainGain, sharedReverbIR);
    this.sidechainGain.connect(this.gainNode);
    this.fx = { autotune: false, eq: false, compressor: false, reverb: false, delay: false, limiter: false };
    // Per-channel FX param state — deep-cloned from FX_PARAM_DEFAULTS so
    // tweaks on one channel never leak to others.
    this.fxParams = {
      eq:         { ...FX_PARAM_DEFAULTS.eq },
      compressor: { ...FX_PARAM_DEFAULTS.compressor },
      limiter:    { ...FX_PARAM_DEFAULTS.limiter },
      reverb:     { ...FX_PARAM_DEFAULTS.reverb },
      delay:      { ...FX_PARAM_DEFAULTS.delay },
      autotune:   { ...FX_PARAM_DEFAULTS.autotune },
    };

    this.gainNode.connect(this.panNode);
    this.panNode.connect(this.analyser);
    this.analyser.connect(masterIn);

    this.muted = false;
    this.solo = false;
    // Per-channel auto-loop + playback rate (set by autoSyncChannels / quantizeChannel).
    this.internalLoop = false;
    this.playbackRate = 1;
    this.volume = 72;
    this.pan = 0;
    // Pitch bend — semitones in [-12, +12]. Applied to source.detune as cents
    // (semi * 100). Default 0. Updated via engine.setChannelPitchBend().
    this.pitchBendSemi = 0;
    // Loaded AudioWorklet plugins, in the order they were inserted into the
    // signal chain. Each entry: { id, url, label, node, ports }. Plugins sit
    // between the FxChain output and `gainNode` — see _rewirePluginChain().
    this.plugins = [];
    this.waveform = null; // downsampled peaks

    // ─── Automation ────────────────────────────────────────────────────
    // Per-channel FX-param-change history. Shape:
    //   { eq: { low: [{t: 1.23, v: 4.5}, ...], mid: [...], high: [...] },
    //     reverb: { wet: [...], decay: [...] },
    //     ... }
    // Points are appended in `setEffectParam` only when (engine.playing && this.automationArmed)
    // — see Channel.recordAutomation. They're sorted ascending by t implicitly because the
    // recording loop only ever appends.
    this.automation = {};
    this.automationArmed = false;
    // Index of the next point to replay per (effect, param). Reset to 0 on every play() and
    // on seek so replay tracks the playhead correctly.
    this._autoCursor = {};
  }

  /** Append an automation point for (name, key) at time `t`. Idempotent w.r.t.
   *  identical consecutive values: if the last recorded value for this param is
   *  the same and was recorded within 30ms, we skip — keeps the curve smooth
   *  without flooding the array during knob drags (which fire ~60 events/sec). */
  recordAutomation(name, key, t, value) {
    const lane = this.automation[name] = this.automation[name] || {};
    const pts = lane[key] = lane[key] || [];
    const last = pts[pts.length - 1];
    if (last && Math.abs(last.v - value) < 1e-4 && (t - last.t) < 0.03) return;
    pts.push({ t, v: value });
  }

  /** Walk automation lanes up to `playhead` time and apply any newly-passed
   *  points to the underlying FX params. Replay state advances via _autoCursor
   *  so each point applies exactly once per playthrough. */
  replayAutomation(playhead, applyParam) {
    for (const [name, params] of Object.entries(this.automation)) {
      for (const [key, pts] of Object.entries(params)) {
        if (!pts.length) continue;
        const cursorKey = `${name}.${key}`;
        let i = this._autoCursor[cursorKey] ?? 0;
        let latest = null;
        // Advance the cursor through every point that has passed.
        while (i < pts.length && pts[i].t <= playhead) {
          latest = pts[i];
          i++;
        }
        this._autoCursor[cursorKey] = i;
        if (latest) applyParam(name, key, latest.v);
      }
    }
  }

  /** Reset replay cursors to align with `time`. Called on every seek / play
   *  so subsequent replayAutomation calls fire from the right starting point. */
  resetAutomationCursor(time = 0) {
    this._autoCursor = {};
    for (const [name, params] of Object.entries(this.automation)) {
      for (const [key, pts] of Object.entries(params)) {
        // Find first index whose time is strictly past `time` — that's where
        // replay should resume. Binary search would be marginally faster on
        // very long sessions but linear is fine for typical session sizes.
        let i = 0;
        while (i < pts.length && pts[i].t < time) i++;
        this._autoCursor[`${name}.${key}`] = i;
      }
    }
  }

  /** Drop all automation for this channel (optionally a single effect/param scope). */
  clearAutomation(name, key) {
    if (!name) {
      this.automation = {};
      this._autoCursor = {};
      return;
    }
    if (!key) {
      delete this.automation[name];
    } else if (this.automation[name]) {
      delete this.automation[name][key];
      if (Object.keys(this.automation[name]).length === 0) delete this.automation[name];
    }
    this._autoCursor = {};
  }

  /** Summary used by the React mirror — what's recorded and how much. */
  getAutomationSummary() {
    let totalPoints = 0;
    const params = [];
    for (const [name, paramGroup] of Object.entries(this.automation)) {
      for (const [key, pts] of Object.entries(paramGroup)) {
        if (!pts.length) continue;
        totalPoints += pts.length;
        params.push({ name, key, count: pts.length });
      }
    }
    return { armed: this.automationArmed, totalPoints, params };
  }

  setEffect(name, enabled) {
    if (!Object.prototype.hasOwnProperty.call(this.fx, name)) return;
    this.fx[name] = enabled;
    this.fxChain.applyEffect(name, enabled, this.fxParams[name]);
  }

  /** Update a single FX param and push it to the live graph immediately.
   *  Called from the Effects Rack knobs; smoothing handled inside applyEffectParams. */
  setEffectParam(name, key, value) {
    const group = this.fxParams[name];
    if (!group || !(key in group)) return;
    group[key] = value;
    // Only re-push to the graph when the effect is actually on (no point
    // smoothing identity values for a bypassed effect).
    if (this.fx[name]) this.fxChain.applyEffect(name, true, group);
  }

  activeEffectCount() {
    return Object.values(this.fx).filter(Boolean).length;
  }

  setVolume(v, anySolo) {
    this.volume = v;
    const effective = (this.muted || (anySolo && !this.solo)) ? 0 : v / 100;
    this.gainNode.gain.setTargetAtTime(effective, this.ctx.currentTime, 0.01);
  }

  setPan(p) {
    this.pan = p;
    this.panNode.pan.setTargetAtTime(p / 50, this.ctx.currentTime, 0.01);
  }

  /** Real-time pitch bend in semitones (range [-12, +12]). Updates every active
   *  audio source's `detune` AudioParam in cents (semis * 100) using a smooth
   *  ramp for click-free changes. Persists so newly-started sources also pick
   *  up the offset (start/_scheduleClips honour `this.pitchBendSemi`). */
  setPitchBend(semi) {
    const clamped = Math.max(-12, Math.min(12, Number(semi) || 0));
    this.pitchBendSemi = clamped;
    const cents = clamped * 100;
    const t = this.ctx.currentTime;
    if (this.source && this.source.detune) {
      try { this.source.detune.setTargetAtTime(cents, t, 0.012); } catch (e) { void e; }
    }
    for (const src of (this.clipSources || [])) {
      if (src && src.detune) {
        try { src.detune.setTargetAtTime(cents, t, 0.012); } catch (e) { void e; }
      }
    }
    for (const voice of (this._activeSynthVoices || [])) {
      for (const osc of (voice.oscs || [])) {
        if (osc && osc.detune) {
          try { osc.detune.setTargetAtTime(cents, t, 0.012); } catch (e) { void e; }
        }
      }
    }
  }

  applyMix(anySolo) {
    const effective = (this.muted || (anySolo && !this.solo)) ? 0 : this.volume / 100;
    this.gainNode.gain.setTargetAtTime(effective, this.ctx.currentTime, 0.01);
  }

  async loadFile(file) {
    const ab = await file.arrayBuffer();
    const buf = await this.ctx.decodeAudioData(ab);
    this.buffer = buf;
    this.name = file.name;
    this.duration = buf.duration;
    this.waveform = downsample(buf, 160);
    return buf;
  }

  loadBuffer(buf, name = "Recording", sourceUrl = null) {
    this.buffer = buf;
    this.name = name;
    this.duration = buf.duration;
    this.waveform = downsample(buf, 160);
    // iter-214 — Remember where this buffer came from so stem extraction
    // can skip the WAV-bounce-and-upload round trip when the audio is
    // already a fal.ai-reachable public URL (Suno / SongLibrary / etc).
    // null when the buffer is from a local recording or MIDI synth.
    this.sourceUrl = sourceUrl || null;
    // Any new audio ends a Recording Studio claim on this channel; the studio re-claims after its own loads.
    this.recOwner = null;
    this.recKey = null;
  }

  /** iter-265b — Set the channel's playbackRate WHILE PLAYING.
   *  Used by the stretch slider's "scrub preview" mode: every slider
   *  tick calls this to give the producer instant audible feedback
   *  (with pitch shift, since playbackRate cheap-stretches via the
   *  Web Audio API) ahead of the SOLA-rendered final pass that lands
   *  ~250 ms after the slider settles.
   *
   *  Sets both:
   *    • `this.playbackRate` — the rate any FUTURE source.start() will use
   *    • `this.source.playbackRate.value` — the LIVE rate of any source
   *      currently playing, via setTargetAtTime for a click-free ramp
   *
   *  Clamped to [0.25, 4] (same envelope as `quantizeChannel`). Outside
   *  that, sources stop generating audio gracefully — but we'd rather
   *  cap than introduce silence.
   */
  setLivePlaybackRate(rate) {
    const clamped = Math.max(0.25, Math.min(4, Number(rate) || 1));
    this.playbackRate = clamped;
    const t = this.ctx.currentTime;
    if (this.source && this.source.playbackRate) {
      try { this.source.playbackRate.setTargetAtTime(clamped, t, 0.02); }
      catch (e) { void e; }
    }
    // Apply to any active clip-sources too so they don't drift out of
    // sync with the primary stem during a scrub preview.
    for (const src of (this.clipSources || [])) {
      if (src && src.playbackRate) {
        try { src.playbackRate.setTargetAtTime(clamped, t, 0.02); }
        catch (e) { void e; }
      }
    }
  }

  // ─── Phase-(c) Clips ────────────────────────────────────────────────
  // Each channel can host arbitrary additional audio "clips" positioned along
  // the bar grid. Clips are independent of `this.buffer` (which remains the
  // channel's PRIMARY sample for backwards-compat) and play alongside it
  // during transport. Clip data shape:
  //   { id, buffer, startBeat, name, durationBeats }
  // We keep `clips: []` allocated on construction (see Channel ctor) so the
  // arrangement view can render an empty row without runtime checks.

  /** Append a new clip. Returns the generated id so callers can address it later. */
  addClip(buf, startBeat = 0, name = "Clip", durationBeats = null) {
    const id = `clip_${++Channel._clipSeq}`;
    const clip = {
      id,
      buffer: buf,
      startBeat: Math.max(0, +startBeat || 0),
      name: name || "Clip",
      // durationBeats is informational — actual playback duration is governed
      // by the buffer length (in seconds) divided by the engine's secPerBeat.
      durationBeats: durationBeats == null ? null : Math.max(0.01, +durationBeats),
    };
    this.clips.push(clip);
    return id;
  }

  /** Move a clip to a new beat position. No-op if id is unknown. */
  moveClip(id, newStartBeat) {
    const c = this.clips.find((x) => x.id === id);
    if (c) c.startBeat = Math.max(0, +newStartBeat || 0);
  }

  /** Drop a clip by id. Returns true on success. */
  removeClip(id) {
    const before = this.clips.length;
    this.clips = this.clips.filter((c) => c.id !== id);
    return this.clips.length < before;
  }

  /** Stop & disconnect every active clip source. Mirrors the primary
   *  `stop()` logic but operates on the `clipSources` array we maintain
   *  per active playback. */
  _stopClipSources() {
    if (!this.clipSources) return;
    this.clipSources.forEach((s) => {
      try { s.stop(); } catch (e) { void e; }
      try { s.disconnect(); } catch (e) { void e; }
    });
    this.clipSources = [];
  }

  /** Schedule every clip given a transport `when` time and current `offset`
   *  (seconds since transport-0). secPerBeat comes from the engine and lets
   *  us convert each clip's startBeat into an absolute time. Clips whose
   *  end-time is before the playhead are skipped; clips ahead of the
   *  playhead start at their natural offset; mid-playing clips start at
   *  their internal offset (offset - clipStart). */
  _scheduleClips(when, offset, secPerBeat) {
    this._stopClipSources();
    this.clipSources = [];
    for (const clip of this.clips) {
      if (!clip.buffer) continue;
      const clipStart = clip.startBeat * secPerBeat;
      const clipEnd = clipStart + clip.buffer.duration;
      if (clipEnd <= offset) continue;          // already past — skip
      const playAt = when + Math.max(0, clipStart - offset);
      const innerOff = offset > clipStart ? (offset - clipStart) : 0;
      const src = this.ctx.createBufferSource();
      src.buffer = clip.buffer;
      if (this.pitchBendSemi) {
        src.detune.value = this.pitchBendSemi * 100;
      }
      this.fxChain.connectPitchTo(src.detune);
      src.connect(this.fxChain.input);
      try { src.start(playAt, innerOff); } catch (e) { void e; }
      this.clipSources.push(src);
    }
  }

  start(when, offset = 0) {
    // Primary buffer — unchanged from iter 1.
    if (this.buffer) {
      this.stop();
      const src = this.ctx.createBufferSource();
      src.buffer = this.buffer;
      if (this.internalLoop) {
        src.loop = true;
        src.loopStart = 0;
        src.loopEnd = this.buffer.duration;
      }
      if (this.playbackRate && this.playbackRate !== 1) {
        src.playbackRate.value = this.playbackRate;
      }
      // Apply current pitch bend (cents = semitones * 100) BEFORE start() so
      // playback opens at the correct pitch — no audible "snap" if the user
      // has dialled in a bend with no audio yet.
      if (this.pitchBendSemi) {
        src.detune.value = this.pitchBendSemi * 100;
      }
      this.fxChain.connectPitchTo(src.detune);
      src.connect(this.fxChain.input);
      src.start(when, offset);
      this.source = src;
    }
    // Phase-(c) — schedule additional clips at their bar positions.
    if (this.clips.length && this.engineSecPerBeat) {
      this._scheduleClips(when, offset, this.engineSecPerBeat);
    }
  }

  stop() {
    if (this.source) {
      try { this.source.stop(); } catch (e) { void e; /* already stopped — non-fatal */ }
      this.fxChain.disconnectPitchFrom(this.source.detune);
      try { this.source.disconnect(); } catch (e) { void e; /* already disconnected — non-fatal */ }
      this.source = null;
    }
    // Phase-(c) — also kill every clip source spawned by `start()`.
    this._stopClipSources();
  }

  getPeak() {
    this.analyser.getByteTimeDomainData(this.peakBuffer);
    let max = 0;
    for (let i = 0; i < this.peakBuffer.length; i++) {
      const v = Math.abs(this.peakBuffer[i] - 128);
      if (v > max) max = v;
    }
    return max / 128; // 0..1
  }
}

/** Compute a 160-point peak envelope of an AudioBuffer's channel 0 — used
 *  by Channel.loadBuffer / loadFile to populate `this.waveform` for the
 *  arrangement view. Linear pass; cost ≈ buffer length / 160.
 *  Exported separately because audioEngine.js re-exports it.
 */
export function downsample(buf, bins) {
  const ch = buf.getChannelData(0);
  const block = Math.floor(ch.length / bins);
  const out = new Array(bins).fill(0);
  for (let i = 0; i < bins; i++) {
    let sum = 0;
    const start = i * block;
    const end = Math.min(start + block, ch.length);
    for (let j = start; j < end; j++) sum += Math.abs(ch[j]);
    out[i] = sum / Math.max(1, end - start);
  }
  const peak = Math.max(...out) || 1;
  return out.map((v) => v / peak);
}
